import { Block, Box, MainContainer } from "../../../styles/basicStyles";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { GetBlogPosts } from "../../../api/requests/blogPosts";
import { useRouter } from "next/router";
import { BlogPost, BlogPageType } from "../../../models/blog";
import { BLOG_POST_LIMIT } from "../../../pages/blog";
import { BlogPostWrapper, BlogWrapper, Line1 } from "./styles";
import InfiniteScroll from "react-infinite-scroller";
import ScrollLoader from "../../../components/views/scrollLoader";
import Filters from "./filters";
import VisibilitySensor from "react-visibility-sensor"; 
import Card from "../../../components/views/card";
import NoRecords from "../../../components/noRecords";

interface Props {
  data: BlogPageType;
  postsProps: {
    items: BlogPost[];
    total: number;
    limit: number;
    page: number;
    pages: number;
  }
}

const Articles: FC<Props> = ({ data, postsProps }) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [started, setStarted] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [lastTotalCount, setLastTotalCount] = useState<number>(0);
  const [filters, setFilters] = useState<string>('');
  const { i18n: { language } } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    setPosts(postsProps.items);
    setHasMore(postsProps.total > postsProps.items.length);
    setLastTotalCount(postsProps.items.length);
    setIsReady(true);
  }, []);

  useEffect(() => {
    if(isReady) {
      setLoading(true);
      loadItems();
    }
  }, [filters]);

  const loadItems = async () => {
    if(hasMore) {
      try {
        const { success, data } = await GetBlogPosts(currentPage + 1, BLOG_POST_LIMIT, filters);

        if(success) {
          const newCases = [...posts, ...data.items];

          let hasMore = newCases.length !== data.total;
          // Security feature - if the total is the same as the last iteration, stop it from trying to get more
          if(lastTotalCount === newCases.length) hasMore = false;

          setPosts(newCases);
          setCurrentPage(currentPage + 1);
          setHasMore(hasMore);
          setLastTotalCount(newCases.length);
          setLoading(false);
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleFilterChange = (filters: string) => {
    setCurrentPage(0);
    setPosts([]);
    setHasMore(true);
    setLastTotalCount(0);
    setFilters(filters);
  };

  const checkState = (isVisible: boolean) => {
    if(isVisible && !started) setStarted(true);
  };

  return (
    <VisibilitySensor onChange={checkState} delayedCall partialVisibility>
      <>
        <MainContainer>
          <Box 
            mt={{ md: 2.938, xxs: 1.563 }}
            fDirection="column" 
            position='relative' 
            zIndex={3}
          >
            <Filters
              categories={data?.categories?.filter(elem => elem.posts > 0) || []}
              handleFilterChange={handleFilterChange}
            />
          </Box>
        </MainContainer>
        {
          // Give a fixed height do loader so the page doensn't jump around when loading
          loading ?
          <Box fAlign="center" fJustify='center' h='100vh' mb={{ md: 2.938, xxs: 1.563 }}>
            <ScrollLoader />
          </Box>
          :
          posts?.length > 0 ?
          <Block w='100%' position='relative' overflow='hidden' mb={{ md: 3.063, xxs: 5.625 }}>
            <Line1 started={started}>
              <svg xmlns="http://www.w3.org/2000/svg" width="1512" height="337" viewBox="0 0 1512 337" fill="none">
                <path d="M-67.0333 319.149C-57.2599 337.615 7.63364 334.092 27.653 334.373C41.785 334.571 58.6824 333.326 71.971 329.448C88.478 324.63 108.018 322.884 123.271 315.477C142.947 305.922 193.753 276.585 182.51 250.98C173.042 229.417 117.961 226.675 96.4885 224.305C78.9162 222.366 10.0297 208.026 10.5406 236.906C10.9039 257.446 79.6445 261.713 95.1621 263.426C129.288 267.193 168.489 252.053 197.213 237.605C229.543 221.343 269.38 204.652 306.219 197.28C337.992 190.922 367.716 176.631 399.522 169.402C437.969 160.664 477.011 153.77 515.544 145.396C604.625 126.037 695.114 117.834 787.675 116.532C871.928 115.348 968.618 110.165 1047.18 142.177C1070.12 151.526 1092.79 159.48 1114.54 171.018C1145.54 187.467 1179.9 191.895 1215.12 195.618C1262.15 200.591 1334.79 196.95 1368.76 164.584C1390.9 143.492 1398.32 112.706 1421.25 91.2979C1446.54 67.6893 1477.79 51.7248 1510.16 36.211C1560.43 12.1215 1599.36 -2.18083 1658.88 4.08843C1685.26 6.86634 1712.3 8.6576 1737.94 15.4053C1749.87 18.5446 1761.51 21.8508 1774 22.5215C1792.55 23.5175 1798.07 18.2404 1812.1 11.1834" stroke="#64FFE3" strokeOpacity="0.9" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </Line1>
            <MainContainer>
              <Block
                w='100%' 
                position='relative' 
                zIndex={2}
              >
                <InfiniteScroll
                  pageStart={0}
                  loadMore={loadItems}
                  hasMore={hasMore}
                  loader={<ScrollLoader key={0} />}
                >
                  <BlogWrapper
                    display="flex"
                    margin={{ xs: '0 -1.125rem', xxs: 0 }}
                  >
                    {
                      posts?.map((post, index) =>
                        <BlogPostWrapper 
                          key={index}
                          w={{ md: "33.333%", xs: "50%", xxs: "100%" }} 
                          padding={{ xs: '0 1.125rem', xxs: 0 }} 
                          margin='1.125 0' 
                        >
                          <Card 
                            imagePath={post.image.url}
                            title={post.category && post.category.name[language]}
                            subtitle={post.title && post.title[language]}
                            author={post.author?.name}
                            date={post.date}
                            onClick={() => router.push(`blog/${post.url}`)}
                          />
                        </BlogPostWrapper>
                      )
                    }
                  </BlogWrapper>
                </InfiniteScroll>
              </Block>
            </MainContainer>
          </Block>
          :
          <MainContainer>
            <NoRecords text='NO_BLOG_POSTS' />
          </MainContainer>
        }
      </>
    </VisibilitySensor>
  );
};

export default Articles;
